import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ContractEmptyState } from './contract-empty-state';
import { ContractErrorState } from './contract-error-state';
import { ContractInstructions } from './contract-instructions';
import { ContractSentState } from './contract-sent-state';
import { ContractSignedState } from './contract-signed-state';
import type { ContractViewData, ContractVisualState } from './contract-state';
import { deriveContractVisualState } from './contract-state';

export interface ContractStepProps {
  contracts: ContractViewData[];
  isLoading?: boolean;
  pendingVersionIds: ReadonlySet<string>;
  resendingVersionIds: ReadonlySet<string>;
  errorsByVersionId: Readonly<Record<string, string>>;
  loadErrorMessage?: string | null;
  onSend?: (versionId: string) => void;
  onRetry?: (versionId: string) => void;
  onResend?: (versionId: string) => void;
  onLoadRetry?: () => void;
}

export function ContractStep({
  contracts,
  isLoading,
  pendingVersionIds,
  resendingVersionIds,
  errorsByVersionId,
  loadErrorMessage,
  onSend,
  onRetry,
  onResend,
  onLoadRetry,
}: ContractStepProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-lg border bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Carregando contratos</span>
      </div>
    );
  }
  if (loadErrorMessage && contracts.length === 0) {
    return <ContractErrorState message={loadErrorMessage} onRetry={onLoadRetry} />;
  }
  if (contracts.length === 0) return <ContractEmptyState />;
  return (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <section key={contract.contractVersionId} className="space-y-2">
          {contracts.length > 1 && (
            <h2 className="text-sm font-semibold text-gray-700">
              {contract.type === 'sim_card' ? 'Contrato SIM Card' : contract.type} · v
              {contract.version}
            </h2>
          )}
          <SingleContractStep
            contract={contract}
            isSending={pendingVersionIds.has(contract.contractVersionId)}
            isResending={resendingVersionIds.has(contract.contractVersionId)}
            errorMessage={errorsByVersionId[contract.contractVersionId]}
            onSend={() => onSend?.(contract.contractVersionId)}
            onRetry={() => onRetry?.(contract.contractVersionId)}
            onResend={() => onResend?.(contract.contractVersionId)}
          />
        </section>
      ))}
    </div>
  );
}

function SingleContractStep({
  contract,
  isSending,
  isResending,
  errorMessage,
  onSend,
  onRetry,
  onResend,
}: {
  contract: ContractViewData;
  isSending: boolean;
  isResending: boolean;
  errorMessage?: string | null;
  onSend?: () => void;
  onRetry?: () => void;
  onResend?: () => void;
}) {
  const [visualState, setVisualState] = useState<ContractVisualState>(() =>
    deriveContractVisualState(contract, isSending),
  );

  useEffect(() => {
    const next = errorMessage ? 'error' : deriveContractVisualState(contract, isSending);
    setVisualState(next);
  }, [contract, errorMessage, isSending]);

  switch (visualState) {
    case 'signed':
      return contract ? <ContractSignedState contract={contract} /> : null;

    case 'waiting_signature':
      return contract ? (
        <ContractSentState contract={contract} isResending={isResending} onResend={onResend} />
      ) : null;

    case 'sending':
      return <ContractInstructions isSending onSend={onSend} disabled />;

    case 'error':
      return (
        <ContractErrorState
          message={errorMessage ?? undefined}
          isRetrying={isSending}
          onRetry={onRetry ?? onSend}
        />
      );
    default:
      return <ContractInstructions isSending={isSending} onSend={onSend} />;
  }
}
